/**
 * QuizComponent - Displays quiz questions with multiple choice options.
 * Shows explanation after submission and reports score.
 */

import { memo, useState, useCallback } from 'react'
import { CheckCircle2, XCircle, HelpCircle } from 'lucide-react'
import { cn } from '../../lib/utils'
import type { QuizContent } from '../../curriculum/manager'

export interface QuizComponentProps {
  /** The quiz content to display */
  content: QuizContent
  /** Callback when quiz is completed with score (0 or 100) */
  onComplete: (score: number) => void
  /** Previous score if already attempted */
  previousScore?: number
  /** Optional className */
  className?: string
}

type QuizState = 'unanswered' | 'answered-correct' | 'answered-incorrect'

/**
 * QuizComponent displays a question with multiple choice answers.
 */
export const QuizComponent = memo(function QuizComponent({
  content,
  onComplete,
  previousScore,
  className,
}: QuizComponentProps) {
  const { question, options, correctOptionId, explanation } = content

  const [selectedOptionId, setSelectedOptionId] = useState<string | null>(null)
  // Always start fresh - we don't store which option was selected, only the score
  // previousScore is used only to show "already passed" status, not to restore state
  const [quizState, setQuizState] = useState<QuizState>('unanswered')

  const isAnswered = quizState !== 'unanswered'
  const isCorrect = quizState === 'answered-correct'
  const alreadyPassed = previousScore !== undefined && previousScore === 100

  // Handle option selection
  const handleOptionSelect = useCallback((optionId: string) => {
    if (quizState === 'unanswered') {
      setSelectedOptionId(optionId)
    }
  }, [quizState])

  // Handle answer submission
  const handleSubmit = useCallback(() => {
    if (!selectedOptionId || isAnswered) return

    const correct = selectedOptionId === correctOptionId
    const score = correct ? 100 : 0

    setQuizState(correct ? 'answered-correct' : 'answered-incorrect')
    onComplete(score)
  }, [selectedOptionId, isAnswered, correctOptionId, onComplete])

  // Handle retry
  const handleRetry = useCallback(() => {
    setSelectedOptionId(null)
    setQuizState('unanswered')
  }, [])

  return (
    <div
      className={cn('rounded-lg border border-border bg-card', className)}
      role="form"
      aria-label="Quiz question"
    >
      {/* Already passed banner */}
      {alreadyPassed && !isAnswered && (
        <div className="border-b border-green-500/30 bg-green-500/10 px-4 py-2">
          <p className="text-sm text-green-700 dark:text-green-400">
            You've already passed this quiz. You can review or skip to the next section.
          </p>
        </div>
      )}

      {/* Question */}
      <div className="border-b border-border p-4">
        <div className="flex items-start gap-3">
          <HelpCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary" />
          <p className="text-base font-medium text-foreground">{question}</p>
        </div>
      </div>

      {/* Options */}
      <div className="space-y-2 p-4">
        {options.map((option) => {
          const isSelected = selectedOptionId === option.id
          const isCorrectOption = option.id === correctOptionId
          const showCorrectness = isAnswered

          let optionStyle = 'border-border bg-background hover:bg-muted/50'
          let iconElement = null

          if (showCorrectness) {
            if (isCorrectOption) {
              optionStyle = 'border-green-500 bg-green-500/10'
              iconElement = <CheckCircle2 className="h-5 w-5 text-green-600" />
            } else if (isSelected && !isCorrectOption) {
              optionStyle = 'border-red-500 bg-red-500/10'
              iconElement = <XCircle className="h-5 w-5 text-red-600" />
            }
          } else if (isSelected) {
            optionStyle = 'border-primary bg-primary/10'
          }

          return (
            <button
              key={option.id}
              type="button"
              onClick={() => handleOptionSelect(option.id)}
              disabled={isAnswered}
              className={cn(
                'flex w-full items-center gap-3 rounded-lg border p-3 text-left transition-colors',
                optionStyle,
                isAnswered && 'cursor-default',
                !isAnswered && !isSelected && 'cursor-pointer'
              )}
              aria-pressed={isSelected}
              aria-disabled={isAnswered}
            >
              {/* Radio indicator */}
              <span
                className={cn(
                  'flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border-2',
                  isSelected
                    ? 'border-primary bg-primary'
                    : 'border-muted-foreground/30 bg-background'
                )}
              >
                {isSelected && !showCorrectness && (
                  <span className="h-2 w-2 rounded-full bg-primary-foreground" />
                )}
              </span>

              {/* Option text */}
              <span className="flex-1 text-sm text-foreground">{option.text}</span>

              {/* Correctness icon */}
              {iconElement}
            </button>
          )
        })}
      </div>

      {/* Explanation (shown after answering) */}
      {isAnswered && (
        <div
          className={cn(
            'border-t border-border p-4',
            isCorrect ? 'bg-green-500/5' : 'bg-red-500/5'
          )}
        >
          <div className="flex items-start gap-3">
            {isCorrect ? (
              <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-600" />
            ) : (
              <XCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-600" />
            )}
            <div>
              <p
                className={cn(
                  'font-medium',
                  isCorrect ? 'text-green-700' : 'text-red-700'
                )}
              >
                {isCorrect ? 'Correct!' : 'Not quite right'}
              </p>
              <p className="mt-1 text-sm text-muted-foreground">{explanation}</p>
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between border-t border-border p-4">
        {isAnswered && !isCorrect ? (
          <button
            type="button"
            onClick={handleRetry}
            className="rounded-md px-4 py-2 text-sm font-medium text-foreground hover:bg-muted"
          >
            Try Again
          </button>
        ) : (
          <div />
        )}

        {!isAnswered && (
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!selectedOptionId}
            className={cn(
              'rounded-md px-4 py-2 text-sm font-medium transition-colors',
              selectedOptionId
                ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                : 'cursor-not-allowed bg-muted text-muted-foreground'
            )}
          >
            Check Answer
          </button>
        )}

        {isAnswered && isCorrect && (
          <span className="text-sm font-medium text-green-600">
            Quiz completed
          </span>
        )}
      </div>
    </div>
  )
})
